/* ============================ TERMINAL (xterm.js 6) + noVNC 1.7 ============================ */
import { useEffect, useRef } from "react";
import { Terminal } from "@xterm/xterm";
import { FitAddon } from "@xterm/addon-fit";
import "@xterm/xterm/css/xterm.css";
import { api, wsUrl } from "../lib/store";

/* A shell into `target` (node:ID, workload:ID, container:ID) over /ws/term. Closes on unmount. */
export function TermBox({ target, height }: { target: string; height?: number | string }) {
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = box.current;
    if (!el) return;
    el.innerHTML = "";
    const term = new Terminal({
      cursorBlink: true, fontFamily: '"JetBrains Mono", ui-monospace, Menlo, Consolas, monospace', fontSize: 13,
      theme: { background: "#101A24", foreground: "#D3E0EC", cursor: "#D3E0EC" }, scrollback: 5000,
    });
    const fit = new FitAddon();
    term.loadAddon(fit);
    term.open(el);
    try { fit.fit(); } catch { /* not laid out yet */ }
    const ws = new WebSocket(wsUrl(`/ws/term?target=${encodeURIComponent(target)}&cols=${term.cols}&rows=${term.rows}`));
    let open = true;
    const onresize = () => {
      try { fit.fit(); if (ws.readyState === 1) ws.send(JSON.stringify({ t: "r", c: term.cols, r: term.rows })); } catch { /* ignore */ }
    };
    const ro = new ResizeObserver(onresize);
    ro.observe(el);
    term.write("\x1b[2mConnecting…\x1b[0m\r\n");
    ws.onopen = () => { onresize(); term.focus(); };
    ws.onmessage = (e) => term.write(e.data);
    ws.onclose = (e) => {
      if (open) term.write(`\r\n\x1b[33m[session closed${e.code === 4403 ? ": Operator role required" : e.code === 4401 ? ": sign in again" : ""}]\x1b[0m\r\n`);
      open = false;
    };
    const sub = term.onData((d) => { if (ws.readyState === 1) ws.send(JSON.stringify({ t: "i", d })); });
    return () => {
      open = false; ro.disconnect(); sub.dispose();
      try { ws.close(); } catch { /* ignore */ }
      try { term.dispose(); } catch { /* ignore */ }
    };
  }, [target]);
  return <div className="term" ref={box} style={height ? { height } : undefined} />;
}

/* The graphical console of a VM (noVNC via /api/workloads/{id}/vnc ticket → /ws/vnc/{id}). */
export function VncBox({ wid, height }: { wid: number | string; height?: number | string }) {
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = box.current;
    if (!el) return;
    let rfb: any = null, open = true;
    const msg = (t: string, color = "#fff") => {
      const p = document.createElement("p");
      p.style.cssText = `color:${color};padding:12px;position:absolute`;
      p.textContent = t;
      el.appendChild(p);
      return p;
    };
    el.innerHTML = "";
    const loadingMsg = msg("Opening console…");
    (async () => {
      let RFB: any;
      try { RFB = (await import("@novnc/novnc")).default; }
      catch (e: any) { loadingMsg.textContent = "noVNC failed to load: " + e.message; return; }
      let t: any;
      try { t = await api("GET", `/api/workloads/${wid}/vnc`); }
      catch (e: any) { loadingMsg.textContent = e.message; return; }
      if (!open) return;
      loadingMsg.remove();
      rfb = new RFB(el, wsUrl(`/ws/vnc/${wid}?port=${t.port}&ticket=${encodeURIComponent(t.ticket)}`),
        { credentials: { password: t.ticket }, wsProtocols: ["binary"] });
      rfb.scaleViewport = true;
      rfb.resizeSession = false;
      rfb.addEventListener("disconnect", () => { if (open) msg("[console disconnected]", "#E8B65A"); open = false; });
    })();
    return () => { open = false; try { rfb && rfb.disconnect(); } catch { /* ignore */ } };
  }, [wid]);
  return <div className="vnc" ref={box} style={{ height, position: "relative" }} />;
}
